<template>
  <div class="card crowded-level-calendar-card">
    <div class="card-header">
      <h3 class="card-title">
        {{ $t('dashboard.crowdLevelCalendar.title') }}
        <span class="d-inline-block" tabindex="0" data-bs-toggle="popover" data-bs-trigger="hover focus" :data-bs-content="$t('dashboard.tooltips.crowdLevelCalendarCard')">
          <i class="bi bi-question-circle ms-2 help-icon"></i>
        </span>
      </h3>
      <p class="subtitle">{{ currentYearMonth }}</p>
    </div>
    <div class="card-body">
      <div class="calendar">
        <table class="calendar-table">
          <thead>
            <tr>
              <th>{{ $t('common.calendar.weekdays.sun') }}</th>
              <th>{{ $t('common.calendar.weekdays.mon') }}</th>
              <th>{{ $t('common.calendar.weekdays.tue') }}</th>
              <th>{{ $t('common.calendar.weekdays.wed') }}</th>
              <th>{{ $t('common.calendar.weekdays.thu') }}</th>
              <th>{{ $t('common.calendar.weekdays.fri') }}</th>
              <th>{{ $t('common.calendar.weekdays.sat') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="week in calendarWeeks" :key="week.id">
              <td
                v-for="day in week.days"
                :key="day.date"
                :class="[day.className, { 'clickable': !day.isEmpty }]"
                @click="handleDayClick(day)"
              >
                {{ day.day }}
              </td>
            </tr>
          </tbody>
        </table>
        <div class="calendar-legend">
          <span class="legend-item">
            <span class="color-box crowded"></span>
            {{ $t('dashboard.crowdLevelCalendar.legend.crowded') }}
          </span>
          <span class="legend-item">
            <span class="color-box crowded-bit"></span>
            {{ $t('dashboard.crowdLevelCalendar.legend.crowdedBit') }}
          </span>
          <span class="legend-item">
            <span class="color-box crowded-moderate"></span>
            {{ $t('dashboard.crowdLevelCalendar.legend.crowdedModerate') }}
          </span>
          <span class="legend-item">
            <span class="color-box crowded-none"></span>
            {{ $t('dashboard.crowdLevelCalendar.legend.crowdedNone') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
    <div class="modal fade" id="crowdLevelDetailModal" tabindex="-1" aria-labelledby="crowdLevelDetailModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="crowdLevelDetailModalLabel">{{ $t('dashboard.crowdLevelCalendar.modal.title') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.crowdLevelCalendar.modal.date') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-value">{{ selectedDayDetails.date }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.crowdLevelCalendar.modal.totalEvents') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-value">{{ selectedDayDetails.totalEvents.toLocaleString() }}{{ $t('dashboard.crowdLevelCalendar.modal.unit') }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.crowdLevelCalendar.modal.eventCapacities') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-value">{{ selectedDayDetails.eventCapacities.toLocaleString() }}{{ $t('dashboard.crowdLevelCalendar.modal.peopleUnit') }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.crowdLevelCalendar.modal.eventParticipants') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-value">{{ selectedDayDetails.eventParticipants.toLocaleString() }}{{ $t('dashboard.crowdLevelCalendar.modal.peopleUnit') }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">{{ $t('dashboard.crowdLevelCalendar.modal.forecastedEventParticipants') }}</span>
              <span class="detail-unit">：</span>
              <span class="detail-value">{{ selectedDayDetails.forecastedEventParticipants.toLocaleString() }}{{ $t('dashboard.crowdLevelCalendar.modal.peopleUnit') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Modal } from 'bootstrap';
import axios from 'axios';
import { colors } from '../../../../consts/colors';

const { t: $t } = useI18n();

const props = defineProps({
  dateRange: {
    type: Object,
    required: true
  }
});

const calendarWeeks = ref([]);
const statisticsData = ref({});
const selectedDayDetails = ref({
  date: '',
  totalEvents: 0,
  eventCapacities: 0,
  eventParticipants: 0,
  forecastedEventParticipants: 0
});

// Modal instance
let modalInstance = null;

// Format date for API (YYYY-MM-DD)
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get current year and month from the first date of the date range
const currentYearMonth = computed(() => {
  if (!props.dateRange || !props.dateRange.startDate) {
    return '';
  }
  const date = props.dateRange.startDate;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}/${month}`;
});

// Generate calendar for the selected month
const generateCalendar = () => {
  if (!props.dateRange || !props.dateRange.startDate) {
    calendarWeeks.value = [];
    return;
  }

  const targetDate = props.dateRange.startDate;
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();

  // Get first day of the month
  const firstDay = new Date(year, month, 1);
  const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 6 = Saturday

  // Get last day of the month
  const lastDay = new Date(year, month + 1, 0);
  const lastDate = lastDay.getDate();

  // Generate weeks
  const weeks = [];
  let currentWeek = [];
  let weekId = 1;

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfWeek; i++) {
    const prevMonthDate = new Date(year, month, -(firstDayOfWeek - i - 1));
    currentWeek.push({
      day: prevMonthDate.getDate().toString(),
      date: formatDate(prevMonthDate),
      className: 'empty',
      isEmpty: true
    });
  }

  // Add days of the month
  for (let day = 1; day <= lastDate; day++) {
    const date = new Date(year, month, day);
    const dateStr = formatDate(date);
    // Use forecasted_event_participants for crowd level determination (includes both actual and forecasted visitors)
    const forecastedEventParticipants = statisticsData.value[dateStr]?.forecasted_event_participants || 0;
    const totalEvents = statisticsData.value[dateStr]?.total_events || 0;

    let className = '';
    if (forecastedEventParticipants >= 100) {
      className = 'event-crowded';
    } else if (forecastedEventParticipants >= 50) {
      className = 'event-crowded-bit';
    } else if (forecastedEventParticipants > 0) {
      className = 'event-crowded-moderate';
    } else if (forecastedEventParticipants === 0 && totalEvents > 0){
      className = 'event-crowded-none';
    }

    currentWeek.push({
      day: day.toString(),
      date: dateStr,
      className,
      isEmpty: false
    });

    // If week is complete (7 days), add to weeks array and start new week
    if (currentWeek.length === 7) {
      weeks.push({
        id: weekId++,
        days: currentWeek
      });
      currentWeek = [];
    }
  }

  // Add empty cells for days after the last day of the month
  if (currentWeek.length > 0) {
    const remainingDays = 7 - currentWeek.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextMonthDate = new Date(year, month + 1, i);
      currentWeek.push({
        day: i.toString(),
        date: formatDate(nextMonthDate),
        className: 'empty',
        isEmpty: true
      });
    }
    weeks.push({
      id: weekId++,
      days: currentWeek
    });
  }

  calendarWeeks.value = weeks;
};

// Fetch statistics data for the month
const fetchStatistics = async () => {
  if (!props.dateRange || !props.dateRange.startDate) {
    return;
  }

  try {
    const targetDate = props.dateRange.startDate;
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();

    // Get first and last day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startDate = formatDate(firstDay);
    const endDate = formatDate(lastDay);

    const response = await axios.get('/api/user/statistics/dashboard/event-calendar', {
      params: {
        start_date: startDate,
        end_date: endDate
      }
    });

    if (response.data) {
      // Convert array to object with date as key
      const dataMap = {};
      response.data.forEach(item => {
        dataMap[item.reference_date] = {
          total_events: item.total_events || 0,
          event_capacities: item.event_capacities || 0,
          event_participants: item.event_participants || 0,
          forecasted_event_participants: item.forecasted_event_participants || 0,
          total_events: item.total_events || 0
        };
      });
      statisticsData.value = dataMap;
    } else {
      statisticsData.value = {};
    }

    // Regenerate calendar with new data
    generateCalendar();
  } catch (error) {
    console.error('Error fetching event calendar statistics:', error);
    statisticsData.value = {};
    generateCalendar();
  }
};

// Handle day click
const handleDayClick = (day) => {
  if (day.isEmpty) {
    return;
  }

  const data = statisticsData.value[day.date] || {
    total_events: 0,
    event_capacities: 0,
    event_participants: 0
  };

  selectedDayDetails.value = {
    date: day.date,
    totalEvents: data.total_events,
    eventCapacities: data.event_capacities,
    eventParticipants: data.event_participants,
    forecastedEventParticipants: data.forecasted_event_participants,
  };

  showDetailModal();
};

// Show detail modal
const showDetailModal = () => {
  if (!modalInstance) {
    const modalElement = document.getElementById('crowdLevelDetailModal');
    if (modalElement) {
      modalInstance = new Modal(modalElement);
    }
  }
  modalInstance?.show();
};

// Watch for date range changes
watch(() => props.dateRange, () => {
  fetchStatistics();
}, { deep: true });

onMounted(() => {
  nextTick(() => {
    fetchStatistics();
  });
});
</script>

<style lang="scss" scoped>
.card {
  background: v-bind('colors.white');
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  margin-bottom: 16px;
}

.card-header {
  background-color: v-bind('colors.white');
  border: none;
  padding: 0;
  margin-bottom: 16px;
}

.card-body {
  padding: 0;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
  margin: 0;

  .help-icon {
    font-size: 18px;
    color: v-bind('colors.textLight');
    cursor: pointer;
  }
}

.crowded-level-calendar-card {
  .subtitle {
    display: block;
    font-size: 16px;
    font-weight: 400;
    color: v-bind('colors.textPrimary');
    margin-top: 4px;
    margin-bottom: 0;
  }

  .calendar-table {
    width: 100%;
    border-collapse: collapse;

    th {
      padding: 8px;
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      color: v-bind('colors.gray[500]');
      border-bottom: 1px solid v-bind('colors.gray[200]');
    }

    td {
      padding: 12px 8px;
      text-align: center;
      font-size: 14px;
      color: v-bind('colors.textPrimary');
      border: 1px solid v-bind('colors.gray[300]');

      &.empty {
        background-color: v-bind('colors.gray[50]');
      }

      &.clickable {
        cursor: pointer;

        &:hover {
          background-color: v-bind('colors.gray[100]');
        }
      }

      &.event-crowded {
        background-color: v-bind('colors.eventCrowded');
        color: v-bind('colors.white');
        font-weight: 600;

        &:hover {
          opacity: 0.9;
        }
      }

      &.event-crowded-bit {
        background-color: v-bind('colors.eventCrowdedBit');
        color: v-bind('colors.textPrimary');
        font-weight: 600;

        &:hover {
          opacity: 0.9;
        }
      }

      &.event-crowded-moderate {
        background-color: v-bind('colors.eventCrowdedModerate');
        color: v-bind('colors.white');
        font-weight: 600;

        &:hover {
          opacity: 0.9;
        }
      }

      &.event-crowded-none {
        background-color: v-bind('colors.eventCrowdedNone');
        color: v-bind('colors.textPrimary');
        font-weight: 600;

        &:hover {
          opacity: 0.9;
        }
      }
    }
  }

  .calendar-legend {
    display: flex;
    gap: 16px;
    margin-top: 12px;
    font-size: 12px;
    color: v-bind('colors.textPrimary');
    justify-content: flex-end;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 4px;

      .color-box {
        width: 16px;
        height: 16px;
        border-radius: 2px;

        &.crowded {
          background-color: v-bind('colors.eventCrowded');
        }

        &.crowded-bit {
          background-color: v-bind('colors.eventCrowdedBit');
        }

        &.crowded-moderate {
          background-color: v-bind('colors.eventCrowdedModerate');
        }

        &.crowded-none {
          background-color: v-bind('colors.eventCrowdedNone');
        }
      }
    }
  }
}

// Modal styles
.modal-content {
  .modal-header {
    background-color: v-bind('colors.white');
    border-bottom: 1px solid v-bind('colors.gray[200]');

    .modal-title {
      font-size: 18px;
      font-weight: 600;
      color: v-bind('colors.textPrimary');
    }
  }

  .modal-body {
    padding: 24px;

    .detail-row {
      display: grid;
      grid-template-columns: 2fr auto 3fr;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid v-bind('colors.gray[100]');
      gap: 8px;

      &:last-child {
        border-bottom: none;
      }

      .detail-label {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        text-align: left;
        white-space: nowrap;
      }

      .detail-unit {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        white-space: nowrap;
      }

      .detail-value {
        font-size: 14px;
        font-weight: 500;
        color: v-bind('colors.textPrimary');
        text-align: right;
      }
    }
  }
}
</style>
